/**
 * Visualizer — small live scope + spectrum widget tapped off the master
 * bus. Reads identically to the OutputMeter (created eagerly, attached
 * post-unlock), so the topbar reserves space at boot and audio wires
 * up the moment the engine is live.
 *
 * Two modes, toggled by clicking the canvas:
 *   - 'scope' (default): time-domain waveform, drawn as a polyline
 *     from `getFloatTimeDomainData`. Reads as an oscilloscope.
 *   - 'spectrum': frequency-domain bars from `getByteFrequencyData`.
 *     Log-spaced bins along the X axis so the bass isn't crammed
 *     into the leftmost pixel.
 *
 * Persisted mode lives in localStorage so the user's choice survives
 * page reloads.
 *
 * Performance:
 *   - rAF loop is the only consumer. We don't redraw if `attach()`
 *     hasn't been called yet, so the suspended-context boot phase
 *     is free.
 *   - Canvas is sized in CSS pixels with a 2× backing-store for
 *     crisp lines on high-DPI displays. Buffer is allocated once
 *     per attach.
 */

import type { AudioEngine } from '@ac-slayer/synth-core';

const STORAGE_KEY = 'evil-synth/visualizer-mode';

export type VisualizerMode = 'scope' | 'spectrum';

/** FFT size — needs to be a power of 2. 2048 gives good frequency
 *  resolution (~23Hz @ 48kHz) without too much per-frame work. */
const FFT_SIZE = 2048;
/** Smoothing for the spectrum mode — non-zero softens the bars so they
 *  don't flicker frame-to-frame. Time-domain reads are unsmoothed
 *  regardless of this setting. */
const SPECTRUM_SMOOTHING = 0.6;

export class Visualizer {
  readonly element: HTMLElement;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx2d: CanvasRenderingContext2D | null;
  private readonly modeLabel: HTMLElement;

  private analyser: AnalyserNode | null = null;
  private timeBuffer: Float32Array | null = null;
  private freqBuffer: Uint8Array | null = null;
  private rafId: number | null = null;

  private mode: VisualizerMode = loadMode();
  private resizeObs: ResizeObserver | null = null;

  constructor() {
    this.element = document.createElement('button');
    this.element.className = 'visualizer';
    (this.element as HTMLButtonElement).type = 'button';
    this.element.setAttribute('aria-label', 'Master output visualizer — click to toggle scope / spectrum');
    this.element.title = 'Click to toggle scope / spectrum';

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'visualizer-canvas';
    // Sized in CSS; backing store sync'd in syncBackingStore().
    this.element.appendChild(this.canvas);
    this.ctx2d = this.canvas.getContext('2d');

    this.modeLabel = document.createElement('span');
    this.modeLabel.className = 'visualizer-mode label';
    this.element.appendChild(this.modeLabel);
    this.renderModeLabel();

    this.element.addEventListener('click', () => this.toggleMode());

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.syncBackingStore());
      this.resizeObs.observe(this.canvas);
    }
  }

  /**
   * Wire to the master bus and start the rAF loop. Idempotent — repeat
   * calls re-create the analyser so the visualizer survives an engine
   * rebuild.
   */
  attach(engine: AudioEngine): void {
    this.detach();
    const ctx = engine.context;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    analyser.smoothingTimeConstant = SPECTRUM_SMOOTHING;
    try {
      engine.master.connect(analyser);
    } catch {
      /* master not ready — caller will attach again later */
    }
    this.analyser = analyser;
    this.timeBuffer = new Float32Array(analyser.fftSize);
    this.freqBuffer = new Uint8Array(analyser.frequencyBinCount);
    this.syncBackingStore();
    this.startLoop();
  }

  /** Stop the loop and release the analyser. Idempotent. */
  detach(): void {
    if (this.rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = null;
    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        /* already disconnected */
      }
      this.analyser = null;
    }
    this.timeBuffer = null;
    this.freqBuffer = null;
    this.clear();
  }

  dispose(): void {
    this.detach();
    this.resizeObs?.disconnect();
    this.resizeObs = null;
  }

  setMode(mode: VisualizerMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    persistMode(mode);
    this.renderModeLabel();
    // Spectrum benefits from internal smoothing; scope doesn't (we want
    // the raw waveform). Keep them on the same setting — drawSpectrum
    // doesn't care whether the byte data is smoothed and scope reads
    // float time-domain which isn't affected by smoothingTimeConstant.
    this.clear();
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private toggleMode(): void {
    this.setMode(this.mode === 'scope' ? 'spectrum' : 'scope');
  }

  private renderModeLabel(): void {
    this.modeLabel.textContent = this.mode === 'scope' ? 'scope' : 'spec';
  }

  private startLoop(): void {
    if (typeof requestAnimationFrame === 'undefined') return;
    const tick = (): void => {
      this.drawFrame();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private syncBackingStore(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    // Avoid 0×0 canvas in case the topbar collapses (mobile portrait
    // with a hidden topbar, etc.) — Canvas APIs throw on zero dims.
    const w = Math.max(2, Math.round(rect.width * dpr));
    const h = Math.max(2, Math.round(rect.height * dpr));
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
  }

  private clear(): void {
    if (!this.ctx2d) return;
    this.ctx2d.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private drawFrame(): void {
    if (!this.ctx2d || !this.analyser) return;
    if (this.mode === 'scope') this.drawScope();
    else this.drawSpectrum();
  }

  private drawScope(): void {
    const ctx = this.ctx2d;
    const buf = this.timeBuffer;
    const analyser = this.analyser;
    if (!ctx || !buf || !analyser) return;
    analyser.getFloatTimeDomainData(buf as unknown as Float32Array<ArrayBuffer>);

    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Center line — a faint reference so silence doesn't look like
    // the visualizer's broken.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();

    // Waveform.
    const accent = readCss('--color-accent', '#e94e77');
    ctx.strokeStyle = accent;
    ctx.lineWidth = Math.max(1, Math.round(window.devicePixelRatio || 1));
    ctx.beginPath();
    const len = buf.length;
    const stepX = w / (len - 1);
    for (let i = 0; i < len; i++) {
      const v = Math.max(-1, Math.min(1, buf[i] ?? 0));
      const x = i * stepX;
      const y = (1 - (v * 0.5 + 0.5)) * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  private drawSpectrum(): void {
    const ctx = this.ctx2d;
    const buf = this.freqBuffer;
    const analyser = this.analyser;
    if (!ctx || !buf || !analyser) return;
    analyser.getByteFrequencyData(buf as unknown as Uint8Array<ArrayBuffer>);

    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Log-frequency mapping: at 48kHz with FFT=2048, bin 0 is DC and
    // the last bin is ~23kHz. A linear x-axis crams every musical
    // octave below ~1kHz into the leftmost few percent of width.
    // Map x ∈ [0, w] → frequency ∈ [20Hz, sampleRate/2] log-scale,
    // then look up the corresponding bin.
    const sampleRate = analyser.context.sampleRate;
    const nyquist = sampleRate / 2;
    const minHz = 20;
    const logMin = Math.log(minHz);
    const logMax = Math.log(nyquist);
    const binCount = buf.length;

    const accent = readCss('--color-accent', '#e94e77');
    ctx.fillStyle = accent;
    // Draw column-by-column so screens with weird DPRs still get
    // crisp bars without anti-aliased gaps.
    for (let x = 0; x < w; x++) {
      const t = x / (w - 1);
      const hz = Math.exp(logMin + (logMax - logMin) * t);
      const bin = Math.min(binCount - 1, Math.max(0, Math.round((hz / nyquist) * binCount)));
      const v = (buf[bin] ?? 0) / 255;
      const barH = v * h;
      ctx.fillRect(x, h - barH, 1, barH);
    }
  }
}

function persistMode(mode: VisualizerMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* private mode / quota — ignore */
  }
}

function loadMode(): VisualizerMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'scope' || stored === 'spectrum') return stored;
  } catch {
    /* private mode — ignore */
  }
  return 'scope';
}

/** Read a CSS custom-property off :root with a fallback. Used so the
 *  visualizer picks up whatever accent color is themed without us
 *  hard-coding it. */
function readCss(varName: string, fallback: string): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}
